import React, { useEffect } from 'react';
import { useLanguage } from '../i18n/LanguageContext';
import { renderNoteHtml } from '../utils/noteRichContent';
import './NoteExpansion.css';

// The rest of a note, shown over the card it belongs to rather than in a
// dialog in the middle of the screen. Opening it is a decision — the "view
// more" click — but closing it is not: moving the mouse away is enough, the
// way a peek should end.
//
// Positioned in document coordinates against the card's rect rather than
// nested inside it, because the board is a CSS multi-column layout and an
// absolutely positioned child of a column can paint under its neighbours.
// The card's own ⋮ menu already takes this approach.
//
// Deliberately the note's own HTML rather than the server's flat preview:
// code blocks, lists and headings all have to survive the trip, and a code
// block cut to its first line is the case this was built for.

interface Props {
  html: string;
  /** The card's bounding rect, in viewport coordinates, when it was opened. */
  anchor: DOMRect;
  onClose: () => void;
  onEdit: () => void;
}

const NoteExpansion: React.FC<Props> = ({ html, anchor, onClose, onEdit }) => {
  const { t } = useLanguage();

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    // A resized window relaid the board out underneath this, so the rect it
    // was placed against no longer describes where the card is.
    document.addEventListener('keydown', onKey);
    window.addEventListener('resize', onClose);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('resize', onClose);
    };
  }, [onClose]);

  return (
    <div
      className="note-expansion"
      role="dialog"
      aria-modal="false"
      style={{
        top: anchor.top + window.scrollY,
        left: anchor.left + window.scrollX,
        width: anchor.width,
        minHeight: anchor.height,
      }}
      onMouseLeave={onClose}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="note-expansion-header">
        {/* Hovering out closes this, but a touch screen has no "out" — so the
            close button is the only way back for most phones and tablets. */}
        <button type="button" className="note-expansion-close" onClick={onClose}>
          {t.note.close}
        </button>
        <button type="button" className="note-expansion-edit" onClick={onEdit}>
          {t.note.edit}
        </button>
      </div>
      <div
        className="note-expansion-body note-content"
        dangerouslySetInnerHTML={{ __html: renderNoteHtml(html) }}
      />
    </div>
  );
};

export default NoteExpansion;
